import { formatDate } from '@/utils/datetime';
// 備品・資産管理 — 経営会議用 コスト＆減価償却レポート（印刷用HTML生成）
const yen = (n) => (typeof n === 'number' ? `¥${Math.round(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—');
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const bar = (w, color) => `<span style="display:inline-block;height:8px;border-radius:4px;background:${color};width:${Math.max(2, Math.round(w * 100))}%"></span>`;

export function groupBy(items, keyFn) {
  const map = {};
  for (const a of items) {
    if (typeof a.cost !== 'number' || a.cost <= 0) continue;
    const k = keyFn(a);
    if (!map[k]) map[k] = { key: k, cost: 0, book: 0, count: 0 };
    map[k].count += 1;
    map[k].cost += a.cost;
    if (typeof a.book_value === 'number') map[k].book += a.book_value;
  }
  return Object.values(map).sort((a, b) => b.cost - a.cost);
}

export function monthlyPurchases(items) {
  const now = new Date();
  const months = [...Array(12)].map((_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `20${String(d.getFullYear()).slice(2)}年${d.getMonth() + 1}月`, cost: 0, count: 0 };
  });
  for (const a of items) {
    if (typeof a.cost !== 'number' || a.cost <= 0) continue;
    const m = months.find((x) => x.key === (a.purchase_date || '').slice(0, 7));
    if (m) { m.cost += a.cost; m.count += 1; }
  }
  return months;
}

export function buildAssetCostReportHtml(items) {
  const today = formatDate(new Date());
  const durables = items.filter((a) => (a.asset_type || 'durable') === 'durable' && a.status !== 'disposed');
  const consumables = items.filter((a) => a.asset_type === 'consumable');
  const withCost = durables.filter((a) => typeof a.cost === 'number' && a.cost > 0);
  const totalCost = withCost.reduce((s, a) => s + a.cost, 0);
  const totalBook = withCost.reduce((s, a) => s + (a.book_value || 0), 0);
  const totalMonthlyDep = withCost.reduce((s, a) => s + (a.monthly_depreciation || 0), 0);
  const consumableCost = consumables.reduce((s, a) => s + (typeof a.cost === 'number' ? a.cost : 0), 0);
  const cats = groupBy(durables, (a) => (a.category || '').trim() || '（カテゴリ未設定）');
  const vendors = groupBy(durables, (a) => (a.vendor || '').trim() || '（ベンダー未設定）');
  const months = monthlyPurchases(items);
  const maxC = Math.max(1, ...cats.map((r) => r.cost));
  const maxV = Math.max(1, ...vendors.map((r) => r.cost));
  const maxM = Math.max(1, ...months.map((m) => m.cost));
  const catRows = cats.map((r) => `<tr><td>${esc(r.key)}</td><td class="c">${r.count}</td><td class="r">${yen(r.cost)}</td><td class="r">${yen(r.book)}</td><td style="width:26%">${bar(r.cost / maxC, '#0052FF')}</td></tr>`).join('');
  const vendorRows = vendors.map((r) => `<tr><td>${esc(r.key)}</td><td class="c">${r.count}</td><td class="r">${yen(r.cost)}</td><td class="r">${yen(r.book)}</td><td style="width:26%">${bar(r.cost / maxV, '#6366f1')}</td></tr>`).join('');
  const monthRows = months.map((m) => `<tr><td>${esc(m.label)}</td><td class="c">${m.count || '—'}</td><td class="r">${m.cost ? yen(m.cost) : '—'}</td><td style="width:32%">${m.cost ? bar(m.cost / maxM, '#0052FF') : ''}</td></tr>`).join('');
  const depRows = withCost
    .slice().sort((a, b) => String(a.purchase_date || '').localeCompare(String(b.purchase_date || '')))
    .map((a) => `
    <tr><td>${esc(a.name)}${a.asset_tag ? `<span style="color:#a1a1aa">（${esc(a.asset_tag)}）</span>` : ''}</td>
    <td>${esc(a.category || '—')}</td>
    <td class="c">${a.purchase_date ? esc(String(a.purchase_date).slice(0, 10)) : '—'}</td>
    <td class="r">${yen(a.cost)}</td>
    <td class="c">${a.useful_life_years ? `${a.useful_life_years}年` : '—'}</td>
    <td class="r">${typeof a.accumulated_depreciation === 'number' ? yen(a.accumulated_depreciation) : '—'}</td>
    <td class="r"><b>${typeof a.book_value === 'number' ? yen(a.book_value) : '—'}</b></td>
    <td class="c">${esc(a.status_label || '')}</td></tr>`).join('');
  return `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><title>資産コストレポート ${today}</title>
<style>
  @page{size:A4;margin:16mm 14mm}
  body{font-family:'Hiragino Kaku Gothic ProN','Yu Gothic',Meiryo,sans-serif;color:#18181b;font-size:11px;margin:0;padding:24px}
  h1{font-size:20px;margin:0}
  h2{font-size:13px;margin:22px 0 6px;padding-bottom:4px;border-bottom:2px solid #0052FF}
  .head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #0052FF;padding-bottom:10px}
  .brand{font-size:11px;color:#0052FF;font-weight:bold;letter-spacing:.1em}
  .meta{font-size:10px;color:#71717a;text-align:right}
  .cards{display:flex;gap:8px;margin-top:14px}
  .card{flex:1;border:1px solid #e4e4e7;border-radius:8px;padding:8px 10px}
  .card p{margin:0;font-size:9px;color:#71717a}
  .card b{font-size:15px}
  table{width:100%;border-collapse:collapse;margin-top:4px}
  th{background:#f4f4f5;text-align:left;font-size:9px;color:#52525b;padding:4px 6px;border:1px solid #e4e4e7}
  td{padding:4px 6px;border:1px solid #e4e4e7;font-size:10px}
  td.r{text-align:right;white-space:nowrap} td.c{text-align:center;white-space:nowrap}
  .note{font-size:9px;color:#a1a1aa;margin-top:16px;border-top:1px solid #e4e4e7;padding-top:6px}
  tr{page-break-inside:avoid}
</style></head><body>
<div class="head">
  <div><div class="brand">SENSBEY／ITシステム管理</div><h1>資産コスト・減価償却レポート</h1></div>
  <div class="meta">作成日: ${today}<br>対象: 備品${durables.length}件${consumables.length ? ` ／ 消耗品${consumables.length}件` : ''}</div>
</div>
<div class="cards">
  <div class="card"><p>備品数（処分済み除く）</p><b>${durables.length}</b></div>
  <div class="card"><p>取得価額合計</p><b style="color:#0052FF">${yen(totalCost)}</b></div>
  <div class="card"><p>現在簿価合計</p><b style="color:#4f46e5">${yen(totalBook)}</b></div>
  <div class="card"><p>月額償却合計</p><b>${yen(totalMonthlyDep)}</b></div>
  <div class="card"><p>消耗品 取得額</p><b>${consumableCost ? yen(consumableCost) : '—'}</b></div>
</div>
<h2>1. カテゴリ別コスト</h2>
<table><tr><th>カテゴリ</th><th>件数</th><th>取得価額</th><th>現在簿価</th><th>構成</th></tr>${catRows || '<tr><td colspan="5" class="c">金額入力のある備品がありません</td></tr>'}</table>
<h2>2. ベンダー別コスト</h2>
<table><tr><th>ベンダー</th><th>件数</th><th>取得価額</th><th>現在簿価</th><th>構成</th></tr>${vendorRows || '<tr><td colspan="5" class="c">金額入力のある備品がありません</td></tr>'}</table>
<h2>3. 月別購入額（過去12ヶ月）</h2>
<table><tr><th>月</th><th>件数</th><th>購入額</th><th>推移</th></tr>${monthRows}</table>
<h2>4. 減価償却明細</h2>
<table><tr><th>資産名</th><th>カテゴリ</th><th>購入日</th><th>取得価額</th><th>耐用年数</th><th>償却累計</th><th>現在簿価</th><th>状態</th></tr>${depRows || '<tr><td colspan="8" class="c">対象資産がありません</td></tr>'}</table>
<p class="note">※ 減価償却は定額法（取得価額－残存価額を耐用年数で均等償却・月割）。耐用年数未入力の資産は償却計算対象外（—表示）。消耗品・処分済み資産は償却集計に含まれません。金額未入力の資産はコスト集計に含まれません。<br>Sensbey Web App（統合業務プラットフォーム）— ITシステム管理 / 備品・資産管理より自動生成</p>
</body></html>`;
}
